/**
 * InvoiceTotals Component - T102
 *
 * Displays invoice totals (net, VAT, gross)
 */
import { calculateDiscountBreakdown, calculateInvoiceTotals as calculateSharedInvoiceTotals, formatCurrency, formatNumber } from '@armard/shared';

interface InvoiceTotalsProps {
  netTotal: number;
  vatRate: number;
  vatAmount: number;
  grossTotal: number;
  overallDiscountPercent: number;
  items: Array<{ lineTotal: string | number }>;
  className?: string;
}

export function InvoiceTotals({
  netTotal,
  vatRate,
  vatAmount,
  grossTotal,
  overallDiscountPercent,
  items,
  className = '',
}: InvoiceTotalsProps) {
  const { subtotal, discount } = calculateDiscountBreakdown(items, netTotal);
  return (
    <div className={`space-y-2 ${className}`}>
      {overallDiscountPercent > 0 && (
        <>
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/50">Zwischensumme</span>
            <span className="text-white">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-white/50">Gesamtrabatt ({formatNumber(overallDiscountPercent, Number.isInteger(overallDiscountPercent) ? 0 : 2)} %)</span>
            <span className="text-white">{discount >= 0 ? '−' : '+'} {formatCurrency(Math.abs(discount))}</span>
          </div>
        </>
      )}
      {/* Net Total */}
      <div className="flex justify-between items-center text-sm">
        <span className="text-white/50">Netto</span>
        <span className="text-white">{formatCurrency(netTotal)}</span>
      </div>

      {/* VAT */}
      <div className="flex justify-between items-center text-sm">
        <span className="text-white/50">MwSt. ({vatRate}%)</span>
        <span className="text-white">{formatCurrency(vatAmount)}</span>
      </div>

      {/* Divider */}
      <div className="border-t border-white/10 pt-2">
        {/* Gross Total */}
        <div className="flex justify-between items-center">
          <span className="text-white/70 font-medium">Brutto</span>
          <span className="text-white text-lg font-semibold">{formatCurrency(grossTotal)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Calculate totals from invoice items
 */
export function calculateInvoiceTotals(
  items: Array<{ lineTotal: string | number }>,
  vatRate: number,
  overallDiscountPercent: number = 0
): {
  subtotal: number;
  overallDiscountAmount: number;
  netTotal: number;
  vatAmount: number;
  grossTotal: number;
} {
  return calculateSharedInvoiceTotals(
    items.map((item) => ({ lineTotal: Number(item.lineTotal) })),
    vatRate,
    overallDiscountPercent
  );
}
